import {type ConnectionOperation, OPTIMISTIC_KEY} from '@github-ui/optimistic-connection'
import {settleConfirmedOperation} from '@github-ui/optimistic-connection/settleConfirmedOperation'
import {notifySettlement} from '@github-ui/optimistic-connection/settlementRegistry'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import {type QueryKey, useQueryClient} from '@github-ui/react-query'
import {updateIssueProjectsMutation as updateIssueProjectsQueryId} from '@github-ui/server-persisted-queries'
import type {UpdateIssueProjectsMutationMutation} from '@github-ui/server-persisted-queries/types/updateIssueProjectsMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

export const PROJECT_ITEMS_CONNECTION_PATH = ['projectItems']
const PENDING_PROJECT_ITEM_ID_PREFIX = 'client:newProjectItem:'

type ProjectItem = NonNullable<
  NonNullable<NonNullable<UpdateIssueProjectsMutationMutation['addProjectV2ItemById']>['projectEdge']>['node']
>
type Project = Omit<ProjectItem['project'], 'template' | 'field'> &
  Partial<Pick<ProjectItem['project'], 'template' | 'field'>>
type AddProjectOperation = Extract<ConnectionOperation, {type: 'addToConnection'}>
type UpdateIssueProjectsCallbacks = {
  onError?: (error: Error) => void
  onCompleted?: (response: UpdateIssueProjectsMutationMutation) => void
}

export function isPendingProjectItemId(id: string): boolean {
  return id.startsWith(PENDING_PROJECT_ITEM_ID_PREFIX)
}

function addProjectOperation(node: ProjectItem): AddProjectOperation {
  return {type: 'addToConnection', connectionPath: PROJECT_ITEMS_CONNECTION_PATH, node, anchor: {at: 'end'}}
}

function projectItemFromOperation(operation: ConnectionOperation): ProjectItem {
  if (operation.type !== 'addToConnection') throw new Error('Expected a project addition')
  return operation.node as ProjectItem
}

function confirmedProjectItem(data: UpdateIssueProjectsMutationMutation): ProjectItem {
  const item = data.addProjectV2ItemById?.projectEdge?.node
  if (!item?.id) throw new Error('Failed to add issue to project')
  return item
}

async function updateIssueProjectsRequest({
  owner,
  repo,
  issueNumber,
  projectId,
  clientMutationId,
}: {
  owner: string
  repo: string
  issueNumber: number
  projectId: string
  clientMutationId?: string
}): Promise<UpdateIssueProjectsMutationMutation> {
  const query = new URLSearchParams({queryId: updateIssueProjectsQueryId})
  const response = await reactFetchJSON(
    `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/issues/${issueNumber}/project_item.json?${query}`,
    {method: 'POST', body: {projectId, clientMutationId}},
  )
  const result = (await response.json()) as {
    data?: UpdateIssueProjectsMutationMutation | null
    errors?: ReadonlyArray<{message?: string}> | null
    error?: string
  } | null
  const responseError = result?.error
  const graphQLError = result?.errors?.find(item => item.message)?.message
  if (!response.ok || responseError || !result?.data) {
    throw new Error(responseError ?? graphQLError ?? 'Failed to add issue to project')
  }
  if (confirmedProjectItem(result.data).project?.id !== projectId) {
    throw new Error('Failed to add issue to project')
  }
  return result.data
}

async function notifyMutationResult(
  mutation: Promise<UpdateIssueProjectsMutationMutation>,
  callbacks?: UpdateIssueProjectsCallbacks,
): Promise<void> {
  let response: UpdateIssueProjectsMutationMutation
  try {
    response = await mutation
  } catch (error) {
    callbacks?.onError?.(error instanceof Error ? error : new Error('Failed to add issue to project'))
    return
  }
  callbacks?.onCompleted?.(response)
}

export function useUpdateIssueProjectsMutation({
  owner,
  repo,
  issueNumber,
  issueId,
  settlementQueryKey,
  clientMutationId,
}: {
  owner: string
  repo: string
  issueNumber: number
  issueId: string
  settlementQueryKey?: QueryKey
  clientMutationId?: string
}) {
  const queryClient = useQueryClient()
  const {mutateAsync} = useConnectionMutation<UpdateIssueProjectsMutationMutation>({
    ownerId: issueId,
    connectionPath: PROJECT_ITEMS_CONNECTION_PATH,
    scope: {id: `update-issue-projects:${issueId}`},
    mutationFn: operation =>
      updateIssueProjectsRequest({
        owner,
        repo,
        issueNumber,
        projectId: projectItemFromOperation(operation).project.id,
        clientMutationId,
      }),
    onSuccess: async (data, {operation: pendingOperation}) => {
      const item = confirmedProjectItem(data)
      const operation = addProjectOperation(item)
      operation.node = {...item, [OPTIMISTIC_KEY]: projectItemFromOperation(pendingOperation).id}
      if (settlementQueryKey) {
        await settleConfirmedOperation({
          queryClient,
          targets: [{queryKey: settlementQueryKey}],
          ownerId: issueId,
          operation,
        })
      }
      await notifySettlement(operation, issueId, PROJECT_ITEMS_CONNECTION_PATH)
    },
  })

  return useCallback(
    (project: Project, callbacks?: UpdateIssueProjectsCallbacks) => {
      const item: ProjectItem = {
        __typename: 'ProjectV2Item',
        id: `${PENDING_PROJECT_ITEM_ID_PREFIX}${project.id}`,
        isArchived: false,
        fieldValueByName: null,
        project: {...project, template: project.template ?? false, field: project.field ?? null},
      }
      void notifyMutationResult(mutateAsync({operation: addProjectOperation(item)}), callbacks)
    },
    [mutateAsync],
  )
}
